import { z } from 'zod';
import type { TechniqueId } from '../rules.js';

/**
 * UMBRAL DE CIERRE — dato configurable, nunca un `if` en el codigo.
 * Cambiar el umbral cambia el comportamiento SIN DESPLEGAR (Fase 5, criterio 1),
 * y la decision se devuelve SIEMPRE con su explicacion.
 */
export const CheckoutThreshold = z.object({
  id: z.string().min(1),
  version: z.string().min(1),
  /** Por encima de este total (con IVA, en centimos) se pide presupuesto. */
  maxTotalCents: z.number().int().positive(),
  /** Por encima de estas unidades se pide presupuesto. */
  maxQty: z.number().int().positive(),
  /** Tecnicas que siempre pasan por comercial (p. ej. bordado: digitalizacion). */
  techniquesRequiringQuote: z.array(z.string()).default([]),
});
export type CheckoutThreshold = z.infer<typeof CheckoutThreshold>;

export type CheckoutDecision = 'PAYMENT' | 'MANUAL_QUOTE';

export interface CheckoutExplanation {
  code:
    | 'WITHIN_THRESHOLDS'
    | 'TOTAL_ABOVE_THRESHOLD'
    | 'QTY_ABOVE_THRESHOLD'
    | 'TECHNIQUE_REQUIRES_QUOTE'
    | 'DESIGN_HAS_BLOCKING_DIAGNOSTICS'
    | 'QUOTE_EXPIRED';
  params: Record<string, string | number | boolean>;
}

export interface CheckoutInput {
  totalCents: number;
  totalQty: number;
  techniqueId: TechniqueId;
  /** true si la validacion tecnica dejo algun diagnostico de severidad `error`. */
  hasBlockingDiagnostics: boolean;
  /** true si el presupuesto sobre el que se decide ya caduco. */
  quoteExpired: boolean;
  threshold: CheckoutThreshold;
}

export interface CheckoutResult {
  decision: CheckoutDecision;
  thresholdApplied: { id: string; version: string; maxTotalCents: number; maxQty: number };
  explanation: CheckoutExplanation;
  /** Todas las razones, no solo la primera: el comercial quiere el cuadro completo. */
  reasons: CheckoutExplanation[];
}

export function decideCheckout(input: CheckoutInput): CheckoutResult {
  const reasons: CheckoutExplanation[] = [];

  if (input.quoteExpired) {
    reasons.push({ code: 'QUOTE_EXPIRED', params: {} });
  }
  if (input.hasBlockingDiagnostics) {
    reasons.push({ code: 'DESIGN_HAS_BLOCKING_DIAGNOSTICS', params: {} });
  }
  if (input.threshold.techniquesRequiringQuote.includes(input.techniqueId)) {
    reasons.push({ code: 'TECHNIQUE_REQUIRES_QUOTE', params: { technique: input.techniqueId } });
  }
  if (input.totalCents > input.threshold.maxTotalCents) {
    reasons.push({
      code: 'TOTAL_ABOVE_THRESHOLD',
      params: { totalCents: input.totalCents, maxTotalCents: input.threshold.maxTotalCents },
    });
  }
  if (input.totalQty > input.threshold.maxQty) {
    reasons.push({ code: 'QTY_ABOVE_THRESHOLD', params: { qty: input.totalQty, maxQty: input.threshold.maxQty } });
  }

  const thresholdApplied = {
    id: input.threshold.id,
    version: input.threshold.version,
    maxTotalCents: input.threshold.maxTotalCents,
    maxQty: input.threshold.maxQty,
  };

  if (reasons.length === 0) {
    const explanation: CheckoutExplanation = {
      code: 'WITHIN_THRESHOLDS',
      params: { totalCents: input.totalCents, qty: input.totalQty },
    };
    return { decision: 'PAYMENT', thresholdApplied, explanation, reasons: [explanation] };
  }

  return { decision: 'MANUAL_QUOTE', thresholdApplied, explanation: reasons[0]!, reasons };
}
